import React, { useEffect, useState, useCallback } from "react";
import { ChevronLeft, ChevronRight } from "lucide-react";
import axios from "axios";

const API_URL = process.env.REACT_APP_BACKEND_URL;

const MONTH_NAMES = ["January", "February", "March", "April", "May", "June", "July", "August", "September", "October", "November", "December"];
const DAY_NAMES = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

/**
 * useFestivals — shared between the customer + admin calendars.
 * Caches the year's festival map keyed by ISO date string. One network round-trip per year.
 */
export const useFestivals = (year) => {
  const [byDate, setByDate] = useState({});
  const [upcoming, setUpcoming] = useState([]);
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const r = await axios.get(`${API_URL}/api/festivals`, { params: { year } });
        if (cancelled) return;
        setByDate(r.data?.by_date || {});
        setUpcoming(r.data?.upcoming || []);
      } catch (e) {
        // Non-fatal — calendars still render without festival markers.
        console.warn('[useFestivals] fetch failed:', e?.message);
      }
    })();
    return () => { cancelled = true; };
  }, [year]);
  return { byDate, upcoming };
};

export const BookingCalendar = ({
  currentMonth, setCurrentMonth, selectedDate, onSelectDate, getDaysInMonth, getDateColor,
}) => {
  const { byDate } = useFestivals(currentMonth.getFullYear());
  const ymPrefix = `${currentMonth.getFullYear()}-${String(currentMonth.getMonth() + 1).padStart(2, '0')}`;
  // All festivals in the displayed month — past or future — so the strip
  // matches the emoji markers on day cells.
  const festivalsThisMonth = Object.entries(byDate)
    .filter(([d]) => d.startsWith(ymPrefix))
    .flatMap(([, list]) => list)
    .sort((a, b) => a.date.localeCompare(b.date));

  return (
    <div className="bg-white rounded-3xl shadow-xl p-4 sm:p-6">
      <div className="flex items-center justify-between mb-4 sm:mb-6">
        <button onClick={() => setCurrentMonth(new Date(currentMonth.getFullYear(), currentMonth.getMonth() - 1))} className="p-2 hover:bg-[#FFFAF5] rounded-full" data-testid="calendar-prev-btn"><ChevronLeft size={24} /></button>
        <h3 className="text-lg sm:text-xl font-bold text-[#1E1B4B]">{MONTH_NAMES[currentMonth.getMonth()]} {currentMonth.getFullYear()}</h3>
        <button onClick={() => setCurrentMonth(new Date(currentMonth.getFullYear(), currentMonth.getMonth() + 1))} className="p-2 hover:bg-[#FFFAF5] rounded-full" data-testid="calendar-next-btn"><ChevronRight size={24} /></button>
      </div>
      <div className="grid grid-cols-7 gap-1 sm:gap-2 mb-4">
        {DAY_NAMES.map(day => (
          <div key={day} className="text-center text-xs sm:text-sm font-semibold text-[#475569] py-1 sm:py-2">{day}</div>
        ))}
      </div>
      <div className="grid grid-cols-7 gap-1 sm:gap-2">
        {getDaysInMonth().map((dayInfo, index) => {
          const fest = dayInfo?.dateStr ? (byDate[dayInfo.dateStr] || [])[0] : null;
          return (
            <button
              key={dayInfo?.dateStr || `empty-${index}`}
              onClick={() => dayInfo && !dayInfo.isPast && !dayInfo.isFullyBooked && onSelectDate(dayInfo.dateStr)}
              disabled={!dayInfo || dayInfo.isPast || dayInfo.isFullyBooked}
              title={fest ? `${fest.emoji} ${fest.name}` : undefined}
              className={`aspect-square rounded-lg sm:rounded-xl text-xs sm:text-sm font-medium transition-all relative ${!dayInfo ? 'invisible' : ''} ${dayInfo ? getDateColor(dayInfo, selectedDate === dayInfo?.dateStr) : ''}`}
              data-testid={dayInfo ? `calendar-day-${dayInfo.day}` : undefined}
            >
              <span>{dayInfo?.day}</span>
              {fest && (
                <span
                  className="absolute top-0.5 right-0.5 text-[10px] sm:text-xs leading-none pointer-events-none"
                  data-testid={`festival-${dayInfo.dateStr}`}
                >
                  {fest.emoji}
                </span>
              )}
            </button>
          );
        })}
      </div>

      {festivalsThisMonth.length > 0 && (
        <div className="mt-5 pt-5 border-t" data-testid="festivals-strip">
          <p className="text-xs font-bold text-[#1E1B4B] mb-2">Festivals this month</p>
          <div className="flex flex-wrap gap-2">
            {festivalsThisMonth.slice(0, 8).map((f) => (
              <span
                key={`${f.date}-${f.key}`}
                className="text-xs bg-[#FFF0F6] text-[#E83E8C] px-2.5 py-1 rounded-full flex items-center gap-1"
                title={f.behaviour_label}
              >
                <span>{f.emoji}</span>
                <span className="font-medium">{f.name}</span>
                <span className="text-[10px] text-[#E83E8C]/70">· {new Date(f.date).toLocaleDateString('en-IN', { day: 'numeric', month: 'short' })}</span>
              </span>
            ))}
          </div>
        </div>
      )}

      <div className="flex flex-wrap justify-center gap-4 mt-6 pt-6 border-t">
        <div className="flex items-center gap-2"><div className="w-4 h-4 rounded bg-green-50 border border-green-200"></div><span className="text-sm text-[#475569]">Available</span></div>
        <div className="flex items-center gap-2"><div className="w-4 h-4 rounded bg-orange-100 border border-orange-200"></div><span className="text-sm text-[#475569]">Filling Fast</span></div>
        <div className="flex items-center gap-2"><div className="w-4 h-4 rounded bg-red-500"></div><span className="text-sm text-[#475569]">Sold Out</span></div>
      </div>
    </div>
  );
};
